/**
 * Shared Batch Attribute Configuration
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig, ActivityContext } from "../../types"
import { findAttributeValue, findAttributeFromActivity, findAttributeFromLinkedActivity } from "../helpers"

export const baseBatchConfig: Partial<AttributeConfig> = {
  attribute: gqlTypes.ActivityAttributeKind.Batch,
  label: "Batch",
  validator: () => ({ valid: true }), // Optional field
  order: 40, // Default order
}

/**
 * Batch for Scheduled status
 * Pre-populated from filling activity (if exists) or order batches, editable
 */
export const batchForScheduled = (): AttributeConfig =>
  ({
    ...baseBatchConfig,
    visibility: "visible",
    editable: true,
    required: false,
    helpText: "Hydrogen batch for this activity",
    // Pre-populate: Try filling activity first, then order batches
    defaultValue: (ctx: ActivityContext) => {
      // First, check if there's a batch from any filling activity
      const fillingBatch = findAttributeFromActivity(
        ctx,
        gqlTypes.ActivityKind.Filling,
        gqlTypes.ActivityAttributeKind.Batch
      )

      if (fillingBatch) {
        return fillingBatch
      }

      // Fallback to order's first batch
      const orderBatch = ctx.order?.batches?.[0]?.id ?? null

      return orderBatch
    },
  } as AttributeConfig)

/**
 * Batch for Started/Completed status
 * Inherited from previous statuses, non-editable
 *
 * @param fromStatuses - Array of statuses to check (in order of preference)
 */
export const batchInherited = (
  fromStatuses: gqlTypes.ActivityEventKind[],
  editable: boolean = false,
  required: boolean = false,
  inheritedStatuses: gqlTypes.ActivityEventKind[] = [
    gqlTypes.ActivityEventKind.Scheduled,
    gqlTypes.ActivityEventKind.Started,
    gqlTypes.ActivityEventKind.Completed,
  ]
): AttributeConfig =>
  ({
    ...baseBatchConfig,
    visibility: "visible",
    editable: editable,
    required: required,
    helpText: "Hydrogen batch for this activity",
    // Inherit from previous statuses (tries in order)
    defaultValue: (ctx: ActivityContext) => {
      const linkedBatch = findAttributeFromLinkedActivity(
        ctx,
        "source",
        gqlTypes.ActivityAttributeKind.Batch,
        inheritedStatuses
      )
      return findAttributeValue(ctx, gqlTypes.ActivityAttributeKind.Batch, fromStatuses) || linkedBatch
    },
  } as AttributeConfig)

/**
 * Batch inherited from another activity (e.g., filling batch for collection)
 *
 * @param fromActivity - Activity kind to inherit from
 * @param fromStatuses - Optional specific statuses to check
 */
export const batchFromActivity = (
  fromActivity: gqlTypes.ActivityKind,
  ...fromStatuses: gqlTypes.ActivityEventKind[]
): AttributeConfig =>
  ({
    ...baseBatchConfig,
    visibility: "visible",
    editable: false,
    required: false,
    helpText: `Batch from ${fromActivity.toLowerCase()} activity`,
    defaultValue: (ctx: ActivityContext) => {
      // If specific statuses provided, try them in order
      if (fromStatuses.length > 0) {
        for (const status of fromStatuses) {
          const value = findAttributeFromActivity(ctx, fromActivity, gqlTypes.ActivityAttributeKind.Batch, status)
          if (value) return value
        }
        return null
      }
      // Otherwise, find from any status (prioritizes Completed)
      return findAttributeFromActivity(ctx, fromActivity, gqlTypes.ActivityAttributeKind.Batch)
    },
  } as AttributeConfig)

/**
 * Batch from filling activity for a specific asset
 * Used for delivery/collection to populate batch based on which asset was filled
 * Falls back to empty (user must select) if no asset-specific filling batch found
 */
export const batchFromAssetFilling = (editable: boolean = true): AttributeConfig =>
  ({
    ...baseBatchConfig,
    visibility: "visible",
    editable: editable,
    required: false,
    helpText: "Batch from filling activity for the selected asset",
    defaultValue: (ctx: ActivityContext) => {
      // Get the selected asset for this delivery/collection activity
      const selectedAssets = ctx.activity.assets

      const selectedAssetId = selectedAssets[0] // Assume single asset for now

      // Find filling activities that used this asset
      const fillingActivities = ctx.activities?.filter((act) => act.kind === gqlTypes.ActivityKind.Filling) || []

      // Look through filling activities to find one that used the selected asset
      for (const fillingActivity of fillingActivities) {
        const activityAssets = fillingActivity.assets

        // Check if this filling activity used the selected asset
        if (activityAssets && activityAssets.some((asset: any) => asset.id === selectedAssetId.id)) {
          // Found a filling activity for this asset, get its batch
          // Try statuses in reverse order (Completed → Started → Scheduled)
          const statuses = [
            gqlTypes.ActivityEventKind.Completed,
            gqlTypes.ActivityEventKind.Started,
            gqlTypes.ActivityEventKind.Scheduled,
          ]

          for (const status of statuses) {
            const batchAttr = fillingActivity.attributes?.find(
              (a) => a.attribute === gqlTypes.ActivityAttributeKind.Batch && a.event_kind === status
            )
            if (batchAttr?.batch?.id) {
              return batchAttr.batch.id
            }
          }
        }
      }
      return null
    },
  } as AttributeConfig)

/**
 * Hidden batch
 */
export const batchHidden: AttributeConfig = {
  ...baseBatchConfig,
  visibility: "hidden",
  editable: false,
  required: false,
} as AttributeConfig
